import { getPublicURLForFile } from '@/common/apiCalls';

import { isPlatformFileId } from '@/views/settings/company/core/companyLogoUpload';

/**
 * @param value - Candidate image URL or file reference.
 */
export function isS3StorageUrl(value: string | undefined | null): boolean {
  const trimmed = value?.trim() ?? '';
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return false;
  }

  try {
    const host = new URL(trimmed).hostname.toLowerCase();
    return host.includes('amazonaws.com') || host.startsWith('s3.') || host.includes('.s3.');
  } catch {
    return false;
  }
}

/**
 * Stored references that must be turned into a signed/public URL before use in `<img src>`.
 *
 * @param reference - `logoLarge` / `logoSmall` value from company preferences.
 */
export function shouldResolveBrandImageViaPublicUrl(
  reference: string | undefined | null
): boolean {
  const trimmed = reference?.trim() ?? '';
  if (!trimmed) {
    return false;
  }

  return isPlatformFileId(trimmed) || isS3StorageUrl(trimmed);
}

/**
 * URLs safe to use directly in `<img src>` (legacy public assets, blob previews).
 *
 * @param url - Resolved or fallback preview URL.
 */
export function isSafeDirectImagePreviewUrl(url: string | undefined | null): boolean {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith('blob:')) {
    return true;
  }

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return false;
  }

  return !isS3StorageUrl(trimmed);
}

/**
 * @param response - `getPublicURLForFile` payload.
 */
export function extractPublicFileUrl(response: unknown): string | null {
  if (response && typeof response === 'object' && 'url' in response) {
    const url = (response as { url: unknown }).url;
    return typeof url === 'string' && url.trim() ? url.trim() : null;
  }

  return null;
}

/**
 * Resolves a platform file id, or legacy `locationURL` / S3 key, to a browser-viewable URL.
 *
 * @param companyID
 * @param employeeID
 * @param token
 * @param fileKey - ObjectId string or stored S3 `locationURL`.
 * @param correlationId
 */
export async function fetchBrandImagePublicUrl(
  companyID: number,
  employeeID: number,
  token: string,
  fileKey: string,
  correlationId: string | null = null
): Promise<string | null> {
  const response = await getPublicURLForFile(
    companyID,
    employeeID,
    token,
    fileKey,
    correlationId
  );

  return extractPublicFileUrl(response);
}
